/**
 * Hook for the user-row "toggle status" action: deactivate / reactivate / activate.
 *
 * Extracted from useUserManagement (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import {
  activateUser,
  deactivateUser,
  reactivateUser,
  type ActivationEligibilityResponse,
  type AvailableLicenseForSeat,
} from '../../services/admin'
import type { User } from '../../types'

interface ToggleDeps {
  activationStatus: Map<string, ActivationEligibilityResponse>
  setError: (msg: string | null) => void
  setSelectedUser: (user: User | null) => void
  setShowSeatModal: (v: boolean) => void
  setUserToReactivate: (user: User | null) => void
  setReactivateLicenses: (lics: AvailableLicenseForSeat[]) => void
  setSelectedLicense: (id: string) => void
  setShowReactivateModal: (v: boolean) => void
  setActionMenuUser: (id: string | null) => void
  loadData: () => Promise<void>
}

export function useToggleStatus(deps: ToggleDeps): (user: User) => Promise<void> {
  const { t } = useTranslation('admin')

  const getEligibilityMessage = (eligibility: string | undefined): string => {
    switch (eligibility) {
      case 'can_activate':
        return t('users.eligibility.canActivate')
      case 'no_organisation':
        return t('users.eligibility.noOrganisation')
      case 'no_seat_assigned':
        return t('users.eligibility.noSeatAssigned')
      case 'seat_revoked':
        return t('users.eligibility.seatRevoked')
      case 'license_inactive':
        return t('users.eligibility.licenseInactive')
      case 'license_not_started':
        return t('users.eligibility.licenseNotStarted')
      case 'license_expired':
        return t('users.eligibility.licenseExpired')
      case 'suspended':
        return t('users.eligibility.suspended')
      default:
        return t('users.eligibility.unknown', { status: eligibility ?? 'undefined' })
    }
  }

  const reactivateSuspended = async (user: User): Promise<void> => {
    const result = await reactivateUser(user.id)
    if (result.success) {
      void deps.loadData()
    } else if (result.availableLicenses && result.availableLicenses.length > 0) {
      deps.setUserToReactivate(user)
      deps.setReactivateLicenses(result.availableLicenses)
      deps.setSelectedLicense('')
      deps.setShowReactivateModal(true)
    } else {
      deps.setError(
        result.message && result.message.length > 0
          ? result.message
          : 'No seats available for reactivation'
      )
    }
  }

  const tryActivatePending = async (user: User): Promise<void> => {
    const status = deps.activationStatus.get(user.id)
    if (status?.canActivate) {
      await activateUser(user.id)
      void deps.loadData()
    } else if (status?.eligibility === 'no_seat_assigned') {
      deps.setSelectedUser(user)
      deps.setShowSeatModal(true)
    } else {
      deps.setError(getEligibilityMessage(status?.eligibility))
    }
  }

  return async (user: User): Promise<void> => {
    try {
      if (user.status === 'active') {
        await deactivateUser(user.id)
        void deps.loadData()
      } else if (user.status === 'suspended') {
        await reactivateSuspended(user)
      } else {
        await tryActivatePending(user)
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error && err.message ? err.message : 'Failed to update user status'
      deps.setError(message)
    }
    deps.setActionMenuUser(null)
  }
}
